<template>
  <div class="poll-vote-list" :class="{ 'poll-dark': $vuetify.theme.dark, 'poll-light': !$vuetify.theme.dark }">
    <template v-if="activePoll">
      <h2 class="poll-title">{{ activePoll.title }}</h2>

      <div class="poll-grid" :style="gridStyle" @mouseleave="hoveredId = null">
        <!-- Sticky header row: track + comment + one cell per category -->
        <div class="poll-cell poll-head poll-track-head" :style="stickyStyle" @mouseenter="hoveredId = null">Track</div>
        <div class="poll-cell poll-head poll-comment-head" :style="stickyStyle" @mouseenter="hoveredId = null"></div>
        <div
          v-for="opt in options"
          :key="'head-' + opt.id"
          class="poll-cell poll-head poll-opt-head"
          :style="stickyStyle"
          @mouseenter="hoveredId = null"
        >
          <span class="poll-opt-label">{{ opt.label }}</span>
        </div>

        <!-- Year labels (chronological sort) + one row per visible track -->
        <template v-for="(track, index) in recordings">
          <!-- YEAR LABEL -->
          <div
            v-if="shouldShowYear(track, index)"
            :key="'year-' + track.id"
            class="poll-year-label font-weight-thin text--disabled d-flex align-center justify-end"
            @click="toggleCollapseExpandYear(track.date.slice(0, 4))"
            @mouseenter="hoveredId = null"
          >
            <v-icon class="mx-2 text--disabled" :small="$vuetify.breakpoint.xs">
              {{ yearsCollapsed.includes(track.date.slice(0, 4)) ? 'mdi-chevron-left' : 'mdi-chevron-down' }}
            </v-icon>
            <span>{{ track.date.slice(0, 4) }}</span>
          </div>

          <!-- TRACK NAME -->
          <div
            v-if="shouldShowTrack(track)"
            :key="'name-' + track.id"
            :id="'trackID' + track.id"
            class="poll-cell poll-track-name"
            :class="{ 'poll-cell-active': isTrackActive(track.id), 'poll-cell-hover': hoveredId === track.id }"
            @click="playTrack(track, index)"
            @mouseenter="hoveredId = track.id"
          >
            {{ track.name }}
          </div>

          <!-- COMMENT BUTTON (one column to the left of the first checkbox) -->
          <div
            v-if="shouldShowTrack(track)"
            :key="'comment-' + track.id"
            class="poll-cell poll-comment-cell"
            :class="{ 'poll-cell-active': isTrackActive(track.id), 'poll-cell-hover': hoveredId === track.id }"
            @mouseenter="hoveredId = track.id"
          >
            <v-btn
              icon
              small
              class="poll-comment-btn"
              :title="hasComment(track.id) ? 'Edit your comment' : 'Add a comment'"
              @click.stop="openComment(track.id)"
            >
              <v-icon small>{{ hasComment(track.id) ? 'mdi-comment-text' : 'mdi-comment-text-outline' }}</v-icon>
            </v-btn>
          </div>

          <!-- VOTE CHECKBOXES -->
          <template v-if="shouldShowTrack(track)">
            <div
              v-for="opt in options"
              :key="track.id + '-' + opt.id"
              class="poll-cell poll-opt-cell"
              :class="{ 'poll-cell-active': isTrackActive(track.id), 'poll-cell-hover': hoveredId === track.id }"
              @mouseenter="hoveredId = track.id"
            >
              <v-checkbox
                :input-value="isSelected(track.id, opt.id)"
                @change="value => toggle(track.id, opt.id, value)"
                class="pollCheckbox"
                hide-details
                dense
                @click.native.stop
              />
            </div>
          </template>
        </template>
      </div>

      <!-- No-results message while searching -->
      <div v-if="isUserTyping && searchResults.length === 0" class="poll-empty">
        No results, please check your search query…
      </div>
    </template>

    <div v-else class="poll-empty">Loading poll…</div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import scrollToTrack from '@/mixins/scrollToTrack';

export default {
  name: 'PollVoteList',

  mixins: [scrollToTrack],

  props: {
    // px offset so the sticky category headers pin just below the fixed player
    stickyTop: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      hoveredId: null,
    };
  },

  computed: {
    ...mapGetters({
      recordings: 'playlist/sortedRecordings',
      activePoll: 'poll/activePoll',
      options: 'poll/pollOptions',
      currentTrackDetails: 'player/currentTrackDetails',
      isPlaying: 'player/isPlaying',
      searchQuery: 'ui/searchQuery',
      searchResults: 'ui/searchResults',
      isUserTyping: 'ui/isUserTyping',
      yearsCollapsed: 'ui/yearsCollapsed',
      shouldShowYearLabels: 'ui/shouldShowYearLabels',
      hasComment: 'comments/hasComment',
    }),

    gridStyle() {
      // Each category column is at least --poll-col-w wide but grows to fit its
      // label (e.g. "RELEASE") so headers never truncate; the track-name column
      // (1fr) absorbs the difference and ellipsizes.
      // A fixed --poll-col-w comment column sits between the name and the first checkbox,
      // so the comment button is exactly one column away (the same gap as between checkboxes).
      const optionCols = this.options.map(() => 'minmax(var(--poll-col-w), max-content)').join(' ');
      return { gridTemplateColumns: `minmax(0, 1fr) var(--poll-col-w) ${optionCols}` };
    },

    stickyStyle() {
      return { top: this.stickyTop + 'px' };
    },
  },

  watch: {
    // Keep the playing track in view as it changes (next/previous/auto-advance
    // and login continuity) — mirrors the regular playlist's behavior. Poll mode
    // is audio-only, so we only need the audio play state. Don't scroll on the
    // initial load or while paused, only when a new track actually starts.
    currentTrackDetails(newTrack, oldTrack) {
      if (!newTrack || !oldTrack || !this.isPlaying) return;
      this.scheduleScrollToTrack(newTrack.id);
    },
  },

  methods: {
    ...mapActions({
      selectAudioTrack: 'player/selectTrack',
      toggleVote: 'poll/toggleVote',
      toggleYearCollapse: 'ui/toggleYearCollapse',
    }),

    isSelected(recordingId, optionId) {
      return this.$store.getters['poll/isOptionSelected'](recordingId, optionId);
    },

    toggle(recordingId, optionId, value) {
      // persist immediately (optimistic); revert handled in the store on failure
      this.toggleVote({ recordingId, optionId, checked: !!value });
    },

    openComment(recordingId) {
      this.$store.dispatch('comments/openEdit', recordingId);
    },

    isTrackActive(trackID) {
      return this.currentTrackDetails && this.currentTrackDetails.id === trackID;
    },

    playTrack(track, index) {
      // Tapping the track name plays it through the normal audio player.
      // `index` is the track's position in the full sortedRecordings list (we
      // filter the view with v-if, not by slicing the array) so the player's
      // indexing stays correct even when years are collapsed or a search is active.
      this.selectAudioTrack({ trackID: track.id, index });
    },

    shouldShowYear(item, index) {
      if (!this.shouldShowYearLabels) {
        return false;
      }
      // Label the start of every contiguous run of a year (same rule as
      // TrackList.shouldShowYear): truthful under any ordering — a track can
      // never render beneath a header of a different year.
      if (index === 0) {
        return true;
      }
      const previous = this.recordings[index - 1];
      return String(previous.date).slice(0, 4) !== String(item.date).slice(0, 4);
    },

    shouldShowTrack(item) {
      if (this.searchQuery !== '') {
        return this.searchResults.includes(item.id);
      }
      return !this.yearsCollapsed.includes(item.date.slice(0, 4));
    },

    toggleCollapseExpandYear(year) {
      this.toggleYearCollapse(year);
    },
  },
};
</script>

<style scoped>
.poll-vote-list {
  --poll-col-w: 64px;
  width: 100%;
  margin-bottom: 150px;
}

.poll-title {
  text-align: center;
  font-size: 1.2rem;
  font-weight: 200;
  margin: 0;
  padding: 10px 30px 0 30px;
  word-break: break-word;
}

.poll-grid {
  display: grid;
  align-items: stretch;
  width: 100%;
}

.poll-cell {
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--v-tertiary-base);
  min-height: 48px;
  box-sizing: border-box;
}

/* Sticky category headers */
.poll-head {
  position: sticky;
  z-index: 4;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.poll-track-head {
  padding-left: 16px;
}

.poll-opt-head {
  justify-content: center;
  text-align: center;
  padding: 4px 8px;
}

.poll-opt-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.1;
}

/* Solid header background so rows don't show through while scrolling */
.poll-light .poll-head {
  background: #fff;
}
.poll-dark .poll-head {
  background: #121212;
}

/* Full-width year section labels (only shown for chronological sort) */
.poll-year-label {
  grid-column: 1 / -1;
  font-size: 50px;
  cursor: pointer;
  user-select: none;
  padding: 4px 16px 4px 0;
}

.poll-track-name {
  padding: 8px 8px 8px 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  min-width: 0;
}

/* Active (currently-playing) row + hover highlight span every cell in the row */
.poll-cell-active {
  background-color: var(--v-tertiary-base);
}

@media (hover: hover) {
  .poll-cell-hover {
    background-color: var(--v-tertiary-base);
  }
}

.poll-opt-cell {
  justify-content: center;
}

.poll-comment-cell {
  justify-content: center;
}

.poll-comment-btn {
  margin: 0 !important;
}

.pollCheckbox {
  margin: 0 !important;
  padding: 0 !important;
}

.pollCheckbox >>> .v-input__slot {
  margin: 0 !important;
}

/* Center the checkbox in its column — override the global 8px right margin,
   but only inside the poll grid (playlist-mode checkboxes keep the 8px). */
.poll-vote-list >>> .v-input--selection-controls__input {
  margin-right: 0;
}

.poll-empty {
  text-align: center;
  margin-top: 40px;
  opacity: 0.7;
}

/* Narrower option columns + smaller text on small screens (down to 320px) */
@media only screen and (max-width: 599px) {
  .poll-vote-list {
    --poll-col-w: 52px;
  }
  .poll-cell {
    min-height: 44px;
  }
  .poll-head {
    font-size: 0.7rem;
  }
  .poll-track-name {
    font-size: 14px;
    padding-left: 12px;
  }
  .poll-track-head {
    padding-left: 12px;
  }
  .poll-year-label {
    font-size: 35px;
  }
}

@media only screen and (max-width: 360px) {
  .poll-vote-list {
    --poll-col-w: 44px;
  }
  .poll-head {
    font-size: 0.62rem;
  }
  .poll-year-label {
    font-size: 28px;
  }
}
</style>
